import {
  emptyParams,
  Filter,
  FindAllParams,
  ListDataSource,
  LocalFindAllParams
} from "./DataSource";
import { Logger, LoggerFactory } from "../logger";
import { action, observable } from "mobx";
import {
  Api,
  defaultsMerger,
  PageableResult,
  RemoteDataSourceOptions,
  Schema
} from "../index";
import { mergeWith } from "lodash";
import { RemoteExecutor } from "./RemoteExecutor";

export class RemoteListDataSource<T, ID> extends RemoteExecutor
  implements ListDataSource<T, ID> {
  public static EXPIRE_AFTER = 1000 * 20;

  @observable public list: T[] = [];
  @observable public selected: ID[] = [];
  @observable public params: LocalFindAllParams;
  @observable public total: number = 0;

  public schema: Schema;
  public name: string;
  public url: string;
  public defaultParams: LocalFindAllParams;
  public log: Logger;
  public api: Api;
  private lastSuccess: number | undefined;

  public constructor({
    api,
    name,
    schema,
    url,
    params,
    commons
  }: RemoteDataSourceOptions) {
    super(name, commons);
    this.schema = schema;
    this.name = name;
    this.log = LoggerFactory.create(`RemoteListDataSource [${this.name}]`);
    this.url = url;
    this.api = api;
    this.defaultParams = this.withMinimalDefaults(params);
    this.params = this.withMinimalDefaults();
    this.log.debug("init", this);
    this.onEvent(`delete.success`, () => this.reload(`delete.success`));
    this.onEvent(`update.success`, () => this.reload(`update.success`));
    this.onEvent(`save.success`, () => this.reload(`save.success`));
  }

  @action
  public async reload(reason: string) {
    this.log.debug("reloading, because: ", reason);
    return this.findAll(this.params, true);
  }

  @action
  public async findAll(
    params?: FindAllParams,
    force = false
  ): Promise<PageableResult<T>> {
    return await this.executeRemote("list.load", async () => {
      if (params && params.filters) {
        params.filters = this.changeUndefinedFieldToEmptySting(params.filters);
      }

      this.log.debug("findAll params", params);
      this.params = this.withMinimalDefaults(params);
      const paramsWithDefaults = this.withDefaults(this.params);
      if (!force && !this.paramsHasChangedOrExpired(paramsWithDefaults)) {
        this.log.debug("findAll returns values from cache");
        return new PageableResult(this.list, this.total);
      }

      const response = await this.api.getList<T>(this.url, {
        params: paramsWithDefaults
      });
      return this.findAllSuccess(response);
    });
  }

  @action
  public async exportAll(type: string, params?: FindAllParams): Promise<void> {
    await this.executeRemote("list.exportAll", async () => {
      if (params && params.filters) {
        params.filters = this.changeUndefinedFieldToEmptySting(params.filters);
      }

      this.params = this.withMinimalDefaults(params);
      const paramsWithDefaults = this.withDefaults(this.params);

      await this.api.fileDownload(this.url, paramsWithDefaults, {
        headers: {
          Accept: type
        }
      });
    });
  }

  @action
  public async exportSelected(type: string, ids: ID[] | ID): Promise<void> {
    await this.executeRemote("list.exportSelected", async () => {
      const idList = this.getIdList(ids).join(",");
      await this.api.fileDownload(this.url + "/" + idList, undefined, {
        headers: {
          Accept: type
        }
      });
    });
  }

  @action
  public async delete(ids: ID[] | ID): Promise<void> {
    await this.executeRemote("delete", async () => {
      const idString = this.createIdList(ids).join(",");
      await this.api.delete(this.url + "/" + idString);
    });
  }

  @action
  public async updateRows(
    ids: ID[] | ID,
    data: {},
    config?: {}
  ): Promise<void> {
    await this.executeRemote("update", async () => {
      const idString = this.createIdList(ids).join(",");
      await this.api.partialUpdate(this.url + "/" + idString, data);
    });
  }

  @action
  public setSelected(id: ID): void {
    this.log.debug("setSelected()");
    this.selected = [...this.selected, id];
  }

  @action
  public clearSelected(): void {
    this.log.debug("clearSelected()");
    this.selected = [];
  }

  @action
  public clearFilters(): void {
    this.log.debug("clearFilters()");
    this.params.filters = [];
  }

  @action
  public toggleSelect(id: ID): void {
    const idx = this.selected.indexOf(id);
    if (idx >= 0) {
      this.selected = this.selected.filter((v, index) => {
        return index !== idx;
      });
    } else {
      this.selected = [...this.selected, id];
    }
  }

  @action
  public toggleSelectOne(id: ID): void {
    const idx = this.selected.indexOf(id);
    if (idx >= 0) {
      this.selected = this.selected.filter((v, index) => {
        return index !== idx;
      });
    } else {
      this.selected = [id];
    }
  }

  @action
  public toggleSelectAll(): void {
    const isEmpty = this.selected.length === 0;

    if (isEmpty) {
      this.selectAll();
    } else {
      this.selected = [];
    }
  }

  @action
  public addOrReplaceDefaultFilter(field: string, value: any): void {
    let updated = false;
    const newFilter: Filter = { field, value };

    this.defaultParams.filters.forEach(f => {
      if (f.field === newFilter.field) {
        f.value = newFilter.value;
        updated = true;
      }
    });

    if (!updated) {
      this.defaultParams.filters.push(newFilter);
    }

    this.list = [];
  }

  @action
  public addOrReplaceFilter(field: string, value: any): void {
    let updated = false;
    const newFilter: Filter = { field, value };

    this.params.filters.forEach(f => {
      if (f.field === newFilter.field) {
        f.value = newFilter.value;
        updated = true;
      }
    });

    if (!updated) {
      this.params.filters.push(newFilter);
    }

    this.list = [];
  }

  @action
  public clear(): void {
    this.log.debug("clear");
    this.triggerEvent("list.clear");
    this.clearSelected();
    this.clearList();
  }

  @action
  public clearList(): void {
    this.list = [];
  }

  @action
  public onEvent(type: string, callback: Function) {
    this.commons.on(`${this.name}.${type}`, callback);
  }

  @action
  protected findAllSuccess(response: PageableResult<T>): PageableResult<T> {
    this.selected = [];
    this.list = response.content;
    this.total = response.totalElements;
    this.lastSuccess = Date.now();

    return response;
  }

  @action
  private reorder = (startIndex: number, endIndex: number) => {
    const [removed] = this.list.splice(startIndex, 1);
    this.list.splice(endIndex, 0, removed);
  };

  @action
  public async moveRow(
    row: T,
    data: { moveTo: number },
    config?: {}
  ): Promise<void> {
    this.reorder(this.list.indexOf(row), data.moveTo);

    try {
      this.api
        .postMethod(
          this.url + "/move" + this.schema.link(row) + "/" + data.moveTo,
          {},
          config
        )
        .then(() => {
          this.reload(`move.success`);
        });
    } catch (e) {
      this.reorder(data.moveTo, this.list.indexOf(row));
    }
  }

  private getIdList(ids: ID[] | ID) {
    const schema = this.schema;
    if (ids instanceof Array) {
      return ids.map(id => {
        return schema.primaryKeyString(id);
      });
    } else if (ids) {
      return [schema.primaryKeyString(ids)];
    } else {
      return [];
    }
  }

  private changeUndefinedFieldToEmptySting = (filters: any[]) => {
    return filters.map(filter => {
      if (filter.value === undefined) {
        filter.value = "";
      }
      return filter;
    });
  };

  private createIdList(ids: ID[] | ID) {
    const schema = this.schema;
    let idList;
    if (ids instanceof Array) {
      idList = ids.map(id => {
        return schema.primaryKeyString(id);
      });
    } else {
      idList = [schema.primaryKeyString(ids)];
    }
    return idList;
  }

  private selectAll(): void {
    // todo: make it type safe
    this.selected = this.list.map(it => it as any);
  }

  private paramsHasChangedOrExpired(params: FindAllParams): boolean {
    if (this.paramsHasExpired()) {
      return true;
    }

    const result = this.params !== params;
    this.log.debug("paramsHasChangedOrExpired => ", result);

    return result;
  }

  private paramsHasExpired() {
    if (!this.lastSuccess) {
      return true;
    }
    return Date.now() - this.lastSuccess > RemoteListDataSource.EXPIRE_AFTER;
  }

  private withDefaults(params?: FindAllParams): LocalFindAllParams {
    const merged = mergeWith({}, this.defaultParams, params, defaultsMerger);
    this.log.debug("params with defaults", merged);

    return merged;
  }

  private withMinimalDefaults(params?: FindAllParams): LocalFindAllParams {
    const minimalDefaults: LocalFindAllParams = {
      ...emptyParams(),
      sort: this.schema.defaultSort || {
        sort: this.schema.fields[0].name,
        order: "asc"
      }
    };

    return mergeWith({}, minimalDefaults, params, defaultsMerger);
  }
}
